import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Loader2, Camera, ImagePlus, X } from "lucide-react";
import { nowISTLocalInput } from "@/lib/dates";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const EMPTY = { client_id: "", bdo_reference_no: "", passenger_name: "", passenger_number: "", number_of_bags: "1", delivery_location: "", delivery_type: "Normal", order_date: nowISTLocalInput() };

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [clientOpen, setClientOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const addPhotos = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPhotos((p) => [...p, ...files].slice(0, 10));
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  useEffect(() => {
    api.get("/clients/options").then((r) => setClients(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/settings/public").then((r) => {
      setForm((f) => ({ ...f, delivery_type: r.data.default_delivery_type || "Normal" }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.client_id) e.client_id = "Please select a client.";
    if (!form.passenger_name.trim()) e.passenger_name = "Passenger name is required.";
    if (!/^[0-9+\-\s()]{7,20}$/.test(form.passenger_number.trim())) e.passenger_number = "Enter a valid phone number (7-20 digits).";
    const bags = parseInt(form.number_of_bags, 10);
    if (!bags || bags < 1) e.number_of_bags = "Number of bags must be at least 1.";
    if (!form.delivery_location.trim()) e.delivery_location = "Delivery location is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        client_id: form.client_id,
        bdo_reference_no: form.bdo_reference_no.trim(),
        passenger_name: form.passenger_name.trim(),
        passenger_number: form.passenger_number.trim(),
        number_of_bags: parseInt(form.number_of_bags, 10),
        delivery_location: form.delivery_location.trim(),
        delivery_type: form.delivery_type,
        order_date: form.order_date || undefined,
      });
      toast.success(`Order ${data.order_id} created successfully.`);
      if (photos.length) {
        try {
          const fd = new FormData();
          photos.forEach((f) => fd.append("files", f));
          await api.post(`/orders/${data.id}/images?kind=pickup`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) {
          toast.error(`Order created, but photo upload failed: ${apiError(e)}`);
        }
      }
      navigate("/orders");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedClient = clients.find((c) => c.id === form.client_id);

  return (
    <div className="max-w-3xl space-y-5" data-testid="create-order-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="create-order-title">Create Order</h1>
        <p className="text-sm text-slate-500 mt-1">New orders start with status <span className="font-semibold text-blue-700">Picked Up</span>.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md" data-testid="create-order-form">
        {/* Client */}
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-4">Client</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Client <span className="text-[#E63946]">*</span></Label>
              <Popover open={clientOpen} onOpenChange={setClientOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" role="combobox" data-testid="client-select-trigger"
                    className={`w-full justify-between h-10 border-slate-300 rounded-sm font-normal ${errors.client_id ? "border-[#E63946]" : ""}`}>
                    {selectedClient ? selectedClient.company_name : "Select a client..."}
                    <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search clients..." data-testid="client-search-input" />
                    <CommandList>
                      <CommandEmpty>No active client found.</CommandEmpty>
                      <CommandGroup>
                        {clients.map((c) => (
                          <CommandItem key={c.id} value={c.company_name} data-testid={`client-option-${c.id}`}
                            onSelect={() => { set("client_id", c.id); setClientOpen(false); }}>
                            <Check className={`h-4 w-4 mr-2 ${form.client_id === c.id ? "opacity-100" : "opacity-0"}`} />
                            {c.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.client_id && <p className="text-xs text-[#E63946]" data-testid="error-client">{errors.client_id}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">BDO / Reference No.</Label>
              <Input value={form.bdo_reference_no} onChange={(e) => set("bdo_reference_no", e.target.value)}
                data-testid="input-reference" placeholder="e.g. BDO-4451 (optional)" className="h-10 border-slate-300 rounded-sm" />
            </div>
          </div>
        </div>

        {/* Passenger */}
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-4">Passenger Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Passenger Name <span className="text-[#E63946]">*</span></Label>
              <Input value={form.passenger_name} onChange={(e) => set("passenger_name", e.target.value)}
                data-testid="input-passenger-name" placeholder="Full name" className={`h-10 border-slate-300 rounded-sm ${errors.passenger_name ? "border-[#E63946]" : ""}`} />
              {errors.passenger_name && <p className="text-xs text-[#E63946]" data-testid="error-passenger-name">{errors.passenger_name}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Passenger Number <span className="text-[#E63946]">*</span></Label>
              <Input value={form.passenger_number} onChange={(e) => set("passenger_number", e.target.value)}
                data-testid="input-passenger-number" placeholder="+91 98XXX XXXXX" className={`h-10 border-slate-300 rounded-sm ${errors.passenger_number ? "border-[#E63946]" : ""}`} />
              {errors.passenger_number && <p className="text-xs text-[#E63946]" data-testid="error-passenger-number">{errors.passenger_number}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Number of Bags <span className="text-[#E63946]">*</span></Label>
              <Input type="number" min="1" value={form.number_of_bags} onChange={(e) => set("number_of_bags", e.target.value)}
                data-testid="input-bags" className={`h-10 border-slate-300 rounded-sm ${errors.number_of_bags ? "border-[#E63946]" : ""}`} />
              {errors.number_of_bags && <p className="text-xs text-[#E63946]" data-testid="error-bags">{errors.number_of_bags}</p>}
            </div>
          </div>
        </div>

        {/* Delivery */}
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-4">Delivery Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2 md:col-span-1">
              <Label className="text-sm font-medium text-slate-700">Delivery Location <span className="text-[#E63946]">*</span></Label>
              <Input value={form.delivery_location} onChange={(e) => set("delivery_location", e.target.value)}
                data-testid="input-location" placeholder="Hotel / terminal / address" className={`h-10 border-slate-300 rounded-sm ${errors.delivery_location ? "border-[#E63946]" : ""}`} />
              {errors.delivery_location && <p className="text-xs text-[#E63946]" data-testid="error-location">{errors.delivery_location}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Delivery Type</Label>
              <Select value={form.delivery_type} onValueChange={(v) => set("delivery_type", v)}>
                <SelectTrigger data-testid="select-delivery-type" className="h-10 border-slate-300 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Normal" data-testid="type-normal">Normal</SelectItem>
                  <SelectItem value="Express" data-testid="type-express">Express</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Order Date</Label>
              <Input type="datetime-local" value={form.order_date} onChange={(e) => set("order_date", e.target.value)}
                data-testid="input-order-date" max={nowISTLocalInput()} className="h-10 border-slate-300 rounded-sm" />
              <p className="text-xs text-slate-400">Defaults to the current date &amp; time (IST).</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Delivery Status</Label>
              <Input value="Picked Up" disabled data-testid="input-status-default" className="h-10 border-slate-200 rounded-sm bg-slate-50 text-blue-700 font-medium" />
            </div>
          </div>
        </div>

        {/* Photos */}
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-4">Order Photos <span className="normal-case font-normal text-slate-400">(optional, max 10)</span></h3>
          <div className="flex items-center gap-3 flex-wrap">
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" data-testid="gallery-input" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-input" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} data-testid="add-gallery-button" className="rounded-sm border-slate-300">
              <ImagePlus className="h-4 w-4 mr-2" /> Add from Gallery
            </Button>
            <Button type="button" variant="outline" onClick={() => cameraRef.current?.click()} data-testid="open-camera-button" className="rounded-sm border-slate-300">
              <Camera className="h-4 w-4 mr-2" /> Open Camera
            </Button>
          </div>
          {photos.length > 0 && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="photo-previews">
              {photos.map((f, i) => (
                <div key={i} className="relative group">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="h-20 w-full object-cover rounded-sm border border-slate-200" />
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#E63946] text-white flex items-center justify-center">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 flex items-center gap-3 flex-wrap">
          <Button type="submit" disabled={submitting} data-testid="submit-order-button"
            className="bg-primary hover:bg-[#152D53] text-white font-semibold rounded-sm px-6 transition-colors duration-150">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Order"}
          </Button>
          <Button type="button" variant="outline" onClick={() => { setForm(EMPTY); setErrors({}); setPhotos([]); }} data-testid="reset-order-button" className="rounded-sm border-slate-300">
            Reset
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-order-button" className="rounded-sm border-slate-300">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
